import { cn } from "@/lib/utils";
import Link from "next/link";
import type React from "react";
import ReactMarkdown from "react-markdown";

export interface RichTextRendererProps {
  content: string;
  className?: string;
  linkClassName?: string;
  textAlign?: "left" | "center" | "right" | "justify";
}

/**
 * Renders markdown content using react-markdown with custom components
 * Supports: # headers, **bold**, [links](url), - lists, --- horizontal rules
 */
export const RichTextRenderer: React.FC<RichTextRendererProps> = ({
  content,
  className,
  linkClassName,
  textAlign = "left",
}) => {
  if (!content) return null;

  return (
    <div
      className={cn(
        "prose prose-invert max-w-none",
        `text-${textAlign}`,
        className,
      )}
    >
      <ReactMarkdown
        components={{
          a: ({ href, children, ...props }) => (
            <Link
              href={href || "#"}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                "text-purple-400 hover:text-purple-300 transition-colors underline",
                linkClassName,
              )}
              {...props}
            >
              {children}
            </Link>
          ),
          h1: ({ children, ...props }) => (
            <h1 className="font-bold mb-6 mt-8 first:mt-0 text-3xl" {...props}>
              {children}
            </h1>
          ),
          h2: ({ children, ...props }) => (
            <h2 className="font-bold mb-6 mt-8 first:mt-0 text-2xl" {...props}>
              {children}
            </h2>
          ),
          h3: ({ children, ...props }) => (
            <h3 className="font-bold mb-6 mt-8 first:mt-0 text-xl" {...props}>
              {children}
            </h3>
          ),
          p: ({ children, ...props }) => (
            <p className="mb-4 last:mb-0" {...props}>
              {children}
            </p>
          ),
          ul: ({ children, ...props }) => (
            <ul
              className="list-disc list-outside mb-4 space-y-1 ml-4"
              {...props}
            >
              {children}
            </ul>
          ),
          li: ({ children, ...props }) => (
            <li className="pl-2" {...props}>
              {children}
            </li>
          ),
          hr: ({ ...props }) => (
            <hr className="my-6 border-gray-600" {...props} />
          ),
          strong: ({ children, ...props }) => (
            <strong className="font-bold" {...props}>
              {children}
            </strong>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default RichTextRenderer;
